import { Link, router } from '@inertiajs/react'
import { useEffect, useRef, useState } from 'react'
import { useOpsMonitor } from './Index'
import InfoTip from '../../../components/InfoTip'

// ─── Helpers ──────────────────────────────────────────────────────────────────

function timeAgo(iso) {
  if (!iso) return '—'
  const sec = Math.round((Date.now() - new Date(iso).getTime()) / 1000)
  if (sec < 60)   return `${sec}s ago`
  if (sec < 3600) return `${Math.round(sec / 60)}m ago`
  return `${Math.round(sec / 3600)}h ago`
}

// ─── Constants ────────────────────────────────────────────────────────────────

const COLUMN_META = {
  queued:          { label: 'Queued',          color: '#FF9800', icon: '⏳', next: 'worker_assigned' },
  worker_assigned: { label: 'Worker Assigned', color: '#2196F3', icon: '👤', next: 'en_route' },
  en_route:        { label: 'En Route',        color: '#9C27B0', icon: '🏃', next: 'arrived' },
  arrived:         { label: 'Arrived',         color: '#00BCD4', icon: '📍', next: 'completed' },
  completed:       { label: 'Completed',       color: '#4CAF50', icon: '✅', next: null },
}

const DISPATCH_TYPE_META = {
  escort:         { icon: '🤝', label: 'Escort' },
  wheelchair:     { icon: '♿', label: 'Wheelchair' },
  luggage:        { icon: '🧳', label: 'Luggage' },
  irop_assist:    { icon: '⚠', label: 'IROP Assist' },
  lounge_transfer:{ icon: '🛋', label: 'Lounge Transfer' },
}

const CARRIER_COLORS = {
  B6: '#003087', DL: '#E01933', UA: '#005DAA', AA: '#0078D2', WN: '#304CB2',
}

// ─── Task card ────────────────────────────────────────────────────────────────

function TaskCard({ task, onAdvance }) {
  const [advancing, setAdvancing] = useState(false)
  const typeM  = DISPATCH_TYPE_META[task.dispatch_type] || { icon: '📋', label: task.dispatch_type }
  const colM   = COLUMN_META[task.status] || COLUMN_META.queued
  const carrier = CARRIER_COLORS[task.carrier_code] || '#607D8B'

  function advance() {
    if (!colM.next || advancing) return
    setAdvancing(true)
    fetch(`/admin/cloudtraq_gds/dispatch/${task.id}/advance`, {
      method:  'POST',
      headers: { 'X-CSRF-Token': document.querySelector('meta[name="csrf-token"]')?.content },
    })
      .then(() => { onAdvance(); setAdvancing(false) })
      .catch(() => setAdvancing(false))
  }

  return (
    <div
      className="bg-white border rounded-lg p-3 shadow-sm hover:shadow-md transition-shadow text-xs"
      style={{ borderLeft: `3px solid ${colM.color}` }}
    >
      {/* Dispatch ref + type */}
      <div className="flex items-start justify-between gap-2 mb-2">
        <div>
          <span className="font-mono font-bold text-gray-900">{task.dispatch_ref}</span>
          <span
            className="ml-1.5 px-1.5 py-0 rounded text-xs font-semibold"
            style={{ background: colM.color + '22', color: colM.color }}
          >
            {typeM.icon} {typeM.label}
          </span>
        </div>
        {task.worker_id && (
          <span className="text-gray-400 font-mono">{task.worker_id}</span>
        )}
      </div>

      {/* PNR + carrier */}
      <div className="flex items-center gap-2 mb-2">
        {task.pnr_locator ? (
          <a
            href={`/admin/cloudtraq_gds/pnr/${task.pnr_locator}`}
            className="font-mono text-indigo-600 hover:underline"
          >
            {task.pnr_locator}
          </a>
        ) : (
          <span className="text-gray-400">—</span>
        )}
        {task.carrier_code && (
          <span className="font-mono font-bold" style={{ color: carrier }}>{task.carrier_code}</span>
        )}
      </div>

      {/* Anchor + ETA */}
      <div className="flex items-center justify-between text-gray-500 mb-2">
        <span>{task.anchor_label || '—'}</span>
        {task.eta_minutes != null && (
          <span className="font-bold tabular-nums text-purple-600">
            ⏱ {task.eta_minutes}min ETA
          </span>
        )}
      </div>

      {/* Created at + advance button */}
      <div className="flex items-center justify-between">
        <span className="text-gray-400">{timeAgo(task.created_at)}</span>
        {colM.next && (
          <button
            onClick={advance}
            disabled={advancing}
            className="text-xs px-2 py-0.5 rounded border font-semibold hover:opacity-80 disabled:opacity-40"
            style={{ borderColor: COLUMN_META[colM.next].color, color: COLUMN_META[colM.next].color }}
          >
            {advancing ? '…' : `→ ${COLUMN_META[colM.next].label}`}
          </button>
        )}
      </div>
    </div>
  )
}

// ─── Kanban column ────────────────────────────────────────────────────────────

function KanbanColumn({ status, tasks, onAdvance }) {
  const meta = COLUMN_META[status]
  return (
    <div className="flex flex-col min-w-0">
      {/* Column header */}
      <div
        className="flex items-center gap-2 px-3 py-2 rounded-t-lg"
        style={{ background: meta.color + '15', borderBottom: `2px solid ${meta.color}` }}
      >
        <span className="text-base">{meta.icon}</span>
        <span className="font-bold text-sm" style={{ color: meta.color }}>{meta.label}</span>
        <span
          className="ml-auto text-xs font-bold w-6 h-6 rounded-full flex items-center justify-center"
          style={{ background: meta.color, color: '#fff' }}
        >
          {tasks.length}
        </span>
      </div>

      {/* Cards */}
      <div className="bg-gray-50 rounded-b-lg p-2 flex-1 space-y-2 min-h-48 overflow-y-auto" style={{ maxHeight: 520 }}>
        {tasks.length === 0 ? (
          <div className="text-center py-6 text-xs text-gray-400">
            No tasks
          </div>
        ) : (
          tasks.map(task => (
            <TaskCard key={task.id} task={task} onAdvance={onAdvance} />
          ))
        )}
      </div>
    </div>
  )
}

// ─── Page ─────────────────────────────────────────────────────────────────────

export default function DispatchBoard({ columns, tenant_branding }) {
  const intervalRef           = useRef(null)
  const [cols, setCols]       = useState(columns)
  const [liveCount, setLiveCount] = useState(0)

  // Sync on prop change
  useEffect(() => { setCols(columns) }, [columns])

  // Auto-reload every 30s
  useEffect(() => {
    intervalRef.current = setInterval(() => {
      router.reload({ only: ['columns'] })
    }, 30_000)
    return () => clearInterval(intervalRef.current)
  }, [])

  // Real-time DISPATCH_STATE_CHANGE
  useOpsMonitor({
    tenantKey: tenant_branding?.tenant_key || 'cloudtraq',
    onEvent: (event) => {
      if (event === 'DISPATCH_STATE_CHANGE') {
        router.reload({ only: ['columns'] })
        setLiveCount(n => n + 1)
      }
    },
  })

  function reload() { router.reload({ only: ['columns'] }) }

  const colOrder   = ['queued', 'worker_assigned', 'en_route', 'arrived', 'completed']
  const totalActive = ['queued', 'worker_assigned', 'en_route', 'arrived']
    .reduce((n, s) => n + (cols[s]?.length || 0), 0)

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="max-w-7xl mx-auto px-4 py-8">

        {/* Header */}
        <div className="flex items-center justify-between mb-6">
          <div>
            <h1 className="text-2xl font-bold text-gray-900">Dispatch Board</h1>
            <p className="text-sm text-gray-500 mt-1">
              Active MSP worker dispatches · {totalActive} active
              {liveCount > 0 && (
                <span className="ml-1.5 text-purple-600 font-semibold">· {liveCount} live updates</span>
              )}
            </p>
          </div>
          <div className="flex items-center gap-2">
            <button onClick={reload} className="px-3 py-1.5 text-sm border border-gray-300 rounded hover:bg-gray-100">
              Refresh
            </button>
            <Link href="/admin/cloudtraq_gds" className="text-sm text-blue-600 hover:underline">← Command Center</Link>
          </div>
        </div>

        {/* Dispatch type legend */}
        <div className="flex gap-3 mb-4 flex-wrap text-xs">
          {Object.entries(DISPATCH_TYPE_META).map(([key, { icon, label }]) => (
            <span key={key} className="flex items-center gap-1 text-gray-500">
              <span>{icon}</span>{label}
            </span>
          ))}
        </div>

        {/* Kanban board */}
        <div className="grid grid-cols-5 gap-3">
          {colOrder.map(status => (
            <KanbanColumn
              key={status}
              status={status}
              tasks={cols[status] || []}
              onAdvance={reload}
            />
          ))}
        </div>

        {/* Nav */}
        <div className="mt-6 flex flex-wrap gap-4 text-sm text-gray-500">
          <Link href="/admin/cloudtraq_gds" className="text-blue-600 hover:underline">← Command Center</Link>
          <Link href="/admin/cloudtraq_gds/high_risk" className="text-blue-600 hover:underline">High Risk Candidates</Link>
          <Link href="/admin/cloudtraq_gds/channel_sensor" className="text-blue-600 hover:underline">channelSensor Ops</Link>
        </div>
      </div>
    </div>
  )
}
